import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { markDeleted } from '../auth/session';
import { fetchPassportData, deleteAccount } from '../api/endpoints';
import { toMessage } from '../utils/errors';
import {
  addressLines,
  formatDate,
  formatNumber,
  fullName,
} from '../utils/format';
import Alert from '../components/Alert';
import Avatar from '../components/Avatar';
import Spinner from '../components/Spinner';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'Ghost Daddy';
// Kept declared so the support block below can be re-enabled by uncommenting.
// eslint-disable-next-line no-unused-vars
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || '';

function DetailRow(props) {
  return (
    <div className="detail">
      <dt className="detail__label">{props.label}</dt>
      <dd className="detail__value">
        {props.children || <span className="detail__empty">Not provided</span>}
      </dd>
    </div>
  );
}

function PointsTile(props) {
  return (
    <div className={'tile tile--' + (props.tone || 'neutral')}>
      <div className="tile__value">{formatNumber(props.value)}</div>
      <div className="tile__label">{props.label}</div>
    </div>
  );
}

export default function Profile() {
  const auth = useAuth();

  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(function () {
    setLoading(true);
    setLoadError(null);

    return fetchPassportData()
      .then(function (data) {
        setProfile(data);
        setLoading(false);
      })
      .catch(function (err) {
        // A 401 already cleared the session and bounces us back to /login.
        setLoadError(toMessage(err, 'Unable to load your account details.'));
        setLoading(false);
      });
  }, []);

  useEffect(
    function () {
      load();
    },
    [load]
  );

  function onConfirmDelete() {
    setDeleting(true);
    setDeleteError(null);

    deleteAccount()
      .then(function () {
        // Flag first, then drop the token — clearing the session immediately
        // re-renders the router and sends us back to /login.
        markDeleted();
        auth.signOut();
      })
      .catch(function (err) {
        setDeleting(false);
        setDeleteError(
          toMessage(
            err,
            'We could not delete your account. Please try again or contact support.'
          )
        );
      });
  }

  const name = fullName(profile) || (auth.user && auth.user.first_name) || '';
  const address = addressLines(profile);

  return (
    <div className="page">
      <header className="topbar">
        <div className="topbar__inner">
          <div className="brand">{APP_NAME}</div>
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={auth.signOut}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="container">
        {loading ? (
          <div className="card loading">
            <Spinner />
            <span>Loading your account details…</span>
          </div>
        ) : null}

        {!loading && loadError ? (
          <div className="card">
            <Alert tone="error">{loadError}</Alert>
            <button
              type="button"
              className="button button--primary"
              onClick={load}
            >
              Try again
            </button>
          </div>
        ) : null}

        {!loading && !loadError && profile ? (
          <React.Fragment>
            <section className="card profile__header">
              <Avatar photo={profile.photo} name={name} profile={profile} />
              <div className="profile__identity">
                <h1 className="profile__name">{name || 'Your account'}</h1>
                {profile.email ? (
                  <p className="profile__email">{profile.email}</p>
                ) : null}
                {profile.passport_number ? (
                  <p className="profile__badge">
                    Passport #{profile.passport_number}
                  </p>
                ) : null}
              </div>
            </section>

            <section className="card">
              <h2 className="card__title">Personal details</h2>
              <p className="card__hint">
                This is the personal data held for your account. All of it is
                removed when you delete your account.
              </p>

              <dl className="details">
                <DetailRow label="First name">{profile.first_name}</DetailRow>
                <DetailRow label="Last name">{profile.last_name}</DetailRow>
                <DetailRow label="Date of birth">
                  {formatDate(profile.dob)}
                </DetailRow>
                <DetailRow label="Email address">{profile.email}</DetailRow>
                <DetailRow label="Phone number">{profile.phone}</DetailRow>
                <DetailRow label="Passport number">
                  {profile.passport_number}
                </DetailRow>
                <DetailRow label="Address">
                  {address.length ? (
                    <span className="detail__stack">
                      {address.map(function (line, index) {
                        return <span key={index}>{line}</span>;
                      })}
                    </span>
                  ) : null}
                </DetailRow>
                <DetailRow label="About">{profile.about}</DetailRow>
                <DetailRow label="Last visit">
                  {formatDate(profile.last_visit)}
                </DetailRow>
              </dl>
            </section>

            <section className="card">
              <h2 className="card__title">Points</h2>
              <div className="tiles">
                <PointsTile label="Earned" value={profile.earned_points} />
                <PointsTile
                  label="Available"
                  value={profile.available_points}
                  tone="positive"
                />
                <PointsTile label="Used" value={profile.used_points} />
                <PointsTile
                  label="Expired"
                  value={profile.expired_points}
                  tone="muted"
                />
              </div>
            </section>

            <section className="card card--danger">
              <h2 className="card__title">Delete account</h2>
              <p className="card__hint">
                Deleting your account permanently removes your profile, photo,
                contact details, visit history and all points shown above. This
                action is immediate and cannot be undone — you will be signed
                out and will need to create a new account to use {APP_NAME}{' '}
                again.
              </p>
              <button
                type="button"
                className="button button--danger"
                onClick={function () {
                  setDeleteError(null);
                  setConfirming(true);
                }}
              >
                Delete my account
              </button>
              {/* Support contact - uncomment once a support mailbox exists,
                  and restore REACT_APP_SUPPORT_EMAIL in .env.
              {SUPPORT_EMAIL ? (
                <p className="card__note">
                  Questions before you delete?{' '}
                  <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>
                </p>
              ) : null}
              */}
            </section>
          </React.Fragment>
        ) : null}
      </main>

      {confirming ? (
        <ConfirmDeleteModal
          busy={deleting}
          error={deleteError}
          onConfirm={onConfirmDelete}
          onCancel={function () {
            if (!deleting) {
              setConfirming(false);
              setDeleteError(null);
            }
          }}
        />
      ) : null}
    </div>
  );
}
